import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, User, KeyRound, FileText, Loader2 } from "lucide-react";

const FIELDS = {
  company: [["company", "Company Name"], ["address", "Address"], ["ssm_no", "SSM No."], ["tin_no", "TIN No."], ["sst_no", "SST Reg No."]],
  pic: [["name", "PIC Name"], ["phone", "PIC Tel No."], ["pic_email", "PIC Email"]],
};

export default function AgentProfile() {
  const { refresh } = useAuth();
  const [p, setP] = useState(null);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/agent/profile").then((r) => setP(r.data));
  useEffect(() => { load(); }, []);
  if (!p) return <div className="text-muted-foreground">Loading...</div>;

  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = Object.fromEntries([...FIELDS.company, ...FIELDS.pic].map(([k]) => [k, p[k] ?? ""]));
      if (pw) body.password = pw;
      await api.patch("/agent/profile", body);
      toast.success("Profile updated");
      setPw("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const uploadSsm = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("ssm_file", f);
    try {
      await api.post("/agent/profile/ssm", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("SSM certificate updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const viewSsm = async () => {
    const r = await api.get("/agent/profile/ssm", { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };

  const section = (Icon, title, keys) => (
    <Card className="p-5 space-y-3">
      <h2 className="font-semibold flex items-center gap-2"><Icon className="w-4 h-4 text-amber-500" /> {title}</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {keys.map(([k, label]) => (
          <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
            <Label>{label}</Label>
            <Input value={p[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`profile-${k.replace(/_/g, "-")}`} />
          </div>
        ))}
      </div>
    </Card>
  );

  return (
    <form onSubmit={save} className="space-y-4 max-w-3xl" data-testid="agent-profile-page">
      <div>
        <h1 className="text-2xl font-display font-bold">My Profile</h1>
        <p className="text-sm text-muted-foreground">Login email: <span className="font-mono" data-testid="profile-email">{p.email}</span></p>
      </div>
      {section(Building2, "Company Details", FIELDS.company)}
      <Card className="p-5 space-y-2">
        <h2 className="font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-amber-500" /> SSM Certificate</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {p.has_ssm_file && <Button type="button" variant="outline" size="sm" onClick={viewSsm} data-testid="profile-view-ssm">View current</Button>}
          <label className="cursor-pointer">
            <input type="file" hidden accept=".pdf,image/*" onChange={uploadSsm} data-testid="profile-upload-ssm" />
            <span className="inline-flex items-center h-9 px-3 rounded-md border text-sm hover:bg-muted">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Replace certificate"}</span>
          </label>
        </div>
      </Card>
      {section(User, "Person In Charge (PIC)", FIELDS.pic)}
      <Card className="p-5 space-y-3">
        <h2 className="font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-500" /> Change Password</h2>
        <div><Label>New password (leave blank to keep current)</Label><Input type="password" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="profile-password" /></div>
      </Card>
      <Button type="submit" disabled={saving} className="bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold" data-testid="profile-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
      </Button>
    </form>
  );
}
